import { isObject } from "lodash";
import React from "react";
import { twJoin } from "tailwind-merge";

import { GenericObject } from "src/api/flowTypes";
import { ExpectedDataCell } from "src/dataTable/ExpectedDataCell";
import { FloatingWindowPlacement, JSONCell } from "src/dataTable/JSONCell";
import { RawCell } from "src/dataTable/RawCell";
import { CellContent, isReactNode } from "src/dataTable/types";
import { isJsonObject } from "src/utils/stringUtils";

export const cellBaseClassName =
  "text-gray-800 font-inter-normal-12px py-2.5 max-w-40 pr-2 truncate" as const;

type Props<T> = {
  cell: CellContent<T> | React.ReactElement;
  objectDetailPosition: "topRight" | "topLeft" | "auto";
  className?: string;
  columnTitle?: string;
  withTooltip?: boolean;
  actualOutput?: { value: string; isMatch: boolean };
  expectedOutput?: { value: string; isMatch: boolean };
  suffix?: React.ReactNode;
};

const detailPositionMap: Record<string, FloatingWindowPlacement> = {
  topRight: "top end",
  topLeft: "top start",
  auto: "auto",
};

export const ResultCell = <T extends GenericObject>({
  cell,
  objectDetailPosition,
  className,
  columnTitle,
  withTooltip = false,
  expectedOutput,
  actualOutput,
  suffix,
}: Props<T>) => {
  const isReactElement = isReactNode(cell);
  const value = !isReactElement ? cell.getValue() : cell;

  if ((expectedOutput || actualOutput) && !isReactElement && value) {
    return (
      <ExpectedDataCell<T>
        actualOutput={actualOutput}
        cell={cell}
        className={cellBaseClassName}
        classNameOverrides={className}
        expectedOutput={expectedOutput}
        suffix={suffix}
      />
    );
  }

  if (
    !isReactElement &&
    !isReactNode(value) &&
    (isJsonObject(value) || isObject(value))
  ) {
    return (
      <JSONCell<T>
        cell={cell}
        className={twJoin(cellBaseClassName, className)}
        columnTitle={columnTitle}
        placement={detailPositionMap[objectDetailPosition] ?? "top start"}
        suffix={suffix}
        value={value}
      />
    );
  } else {
    return (
      <RawCell<T>
        cell={cell}
        className={cellBaseClassName}
        classNameOverrides={className}
        suffix={suffix}
        withTooltip={withTooltip}
      />
    );
  }
};
